/**
 * useEscalations Hook
 * 
 * Fetches and manages escalation data from the backend API.
 * Replaces mock data in PendingApprovalsQueue and other components.
 */

import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../lib/env';
import { auth } from '../lib/firebase';

const API_BASE = getApiUrl();

/**
 * Get current Firebase ID token for authenticated requests.
 */
async function getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
        console.debug('[useEscalations] No authenticated user');
        return null;
    }
    try {
        const token = await user.getIdToken(false);
        return token;
    } catch (error) {
        console.error('[useEscalations] Failed to get Firebase token:', error);
        return null;
    }
}

// ============================================================================
// TYPES
// ============================================================================

export interface Escalation {
    id: string;
    replicaId: string;
    replicaName?: string;
    decisionId: string;
    decisionType: string;
    decisionContent: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
    isBlocking: boolean;
    urgency: 'immediate' | 'soon' | 'normal';
    expiresAt: string | null;
    createdAt: string;
    resolvedAt: string | null;
    resolverId: string | null;
    feedback: string | null;
    // Computed for display
    riskLevel: 'high' | 'medium' | 'low';
    proposedAt: string; // Relative time string
}

export interface EscalationFilters {
    status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
    replicaId?: string;
    urgency?: string;
}

export interface UseEscalationsResult {
    escalations: Escalation[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    approve: (id: string, feedback?: string) => Promise<boolean>;
    reject: (id: string, feedback?: string) => Promise<boolean>;
    hasBlockingEscalations: (replicaId: string) => Promise<boolean>;
}

// ============================================================================
// UTILITIES
// ============================================================================

function getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

function mapUrgencyToRisk(urgency: string): 'high' | 'medium' | 'low' {
    switch (urgency) {
        case 'immediate': return 'high';
        case 'soon': return 'medium';
        default: return 'low';
    }
}

// ============================================================================
// HOOK
// ============================================================================

export function useEscalations(filters?: EscalationFilters): UseEscalationsResult {
    const [escalations, setEscalations] = useState<Escalation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchEscalations = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            if (filters?.status) params.append('status', filters.status);
            if (filters?.replicaId) params.append('replicaId', filters.replicaId);

            const token = await getAuthToken();
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/escalations?${params.toString()}`, {
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch escalations: ${response.status}`);
            }

            const data = await response.json();

            // Transform API response to frontend format
            const transformed: Escalation[] = (data.escalations || []).map((e: {
                id: string;
                replicaId: string;
                replica?: { name: string };
                decisionId: string;
                decisionType: string;
                decisionContent: string;
                status: Escalation['status'];
                isBlocking: boolean;
                urgency: Escalation['urgency'];
                expiresAt: string | null;
                createdAt: string;
                resolvedAt: string | null;
                resolverId: string | null;
                feedback: string | null;
            }) => ({
                ...e,
                replicaName: e.replica?.name || 'Unknown Replica',
                riskLevel: mapUrgencyToRisk(e.urgency),
                proposedAt: getRelativeTime(e.createdAt),
            }));

            setEscalations(transformed);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [filters?.status, filters?.replicaId]);

    useEffect(() => {
        fetchEscalations();
    }, [fetchEscalations]);

    const approve = useCallback(async (id: string, feedback?: string): Promise<boolean> => {
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/escalations/${id}/approve`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ feedback: feedback || 'Approved' }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to approve');
            }

            // Remove from local state
            setEscalations(prev => prev.filter(e => e.id !== id));
            return true;
        } catch (err) {
            console.error('Approve error:', err);
            return false;
        }
    }, []);

    const reject = useCallback(async (id: string, feedback?: string): Promise<boolean> => {
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/escalations/${id}/reject`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ feedback: feedback || 'Rejected' }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to reject');
            }

            // Remove from local state
            setEscalations(prev => prev.filter(e => e.id !== id));
            return true;
        } catch (err) {
            console.error('Reject error:', err);
            return false;
        }
    }, []);

    const hasBlockingEscalations = useCallback(async (replicaId: string): Promise<boolean> => {
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/escalations/blocking/${replicaId}`, {
                headers,
            });

            if (!response.ok) return false;

            const data = await response.json();
            return data.hasBlocking || false;
        } catch {
            return false;
        }
    }, []);

    return {
        escalations,
        loading,
        error,
        refetch: fetchEscalations,
        approve,
        reject,
        hasBlockingEscalations,
    };
}

// ============================================================================
// GUARDIAN HOOK
// ============================================================================

export interface GuardianInvitation {
    id: string;
    replicaOwnerId: string;
    ownerEmail: string;
    replicaId: string | null;
    invitedAt: string;
}

export function useGuardianInvitations() {
    const [invitations, setInvitations] = useState<GuardianInvitation[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchInvitations = useCallback(async () => {
        setLoading(true);
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/guardians/pending-invitations`, {
                headers,
            });

            if (response.ok) {
                const data = await response.json();
                setInvitations(data.invitations || []);
            }
        } catch (err) {
            console.error('Failed to fetch guardian invitations:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    const accept = useCallback(async (id: string): Promise<boolean> => {
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/guardians/${id}/accept`, {
                method: 'POST',
                headers,
            });

            if (response.ok) {
                setInvitations(prev => prev.filter(i => i.id !== id));
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    const decline = useCallback(async (id: string): Promise<boolean> => {
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/guardians/${id}/decline`, {
                method: 'POST',
                headers,
            });

            if (response.ok) {
                setInvitations(prev => prev.filter(i => i.id !== id));
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    return { invitations, loading, refetch: fetchInvitations, accept, decline };
}

export default useEscalations;
